import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Luggage, Plane, Tag as TagIcon, MapPin, User } from "lucide-react";
import api from "../lib/api";
import BrandMark from "../components/BrandMark";

/* Public read-only page for a lost bag. The QR on the lost-bag PDF and the
   Manifest PDF both point here so airline lost-and-found staff can scan and
   see who to return the bag to. No auth required — minimal PII only. */

export default function PublicBag() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/wallet/bags/public/${token}`).then((r) => setB(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center shadow-warm-md" data-testid="public-bag-error">
        <BrandMark size="md" testid="public-bag-brand" />
        <p className="mt-6 text-lg font-serif text-primary">This return link is not valid.</p>
        <p className="text-sm text-muted-foreground mt-2">It may have been revoked by the owner.</p>
      </div>
    </div>
  );
  if (!b) return <div className="min-h-screen bg-background" />;

  const flight = [b.airline, b.flight_number].filter(Boolean).join(" ") || "—";
  return (
    <div className="min-h-screen bg-background" data-testid="public-bag-page">
      <div className="max-w-lg mx-auto px-5 py-8">
        <BrandMark size="md" testid="public-bag-brand" />
        <div className="mt-6 rounded-2xl bg-card border border-border p-6 shadow-warm-md">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-accent">Please return this bag to</div>
          <div className="mt-1 font-serif font-800 text-3xl text-primary flex items-center gap-2" data-testid="public-bag-owner">
            <User className="w-6 h-6 text-primary/70" /> {b.owner_name}
          </div>
          {b.contact_email && (
            <a href={`mailto:${b.contact_email}?subject=Bag%20found%20-%20${encodeURIComponent(b.owner_name)}`}
              data-testid="public-bag-contact"
              className="mt-3 inline-flex items-center px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-700 hover:opacity-95 tap">
              Email {b.contact_name || "the owner"}
            </a>
          )}
        </div>

        <div className="mt-4 rounded-2xl bg-card border border-border p-5 space-y-3" data-testid="public-bag-details">
          <Row icon={Luggage} label="Bag" value={`${b.bag_count || 1} × ${b.bag_type}`} />
          <Row icon={Plane} label="Flight" value={flight} />
          <Row icon={MapPin} label="Route" value={`${b.from_airport || "?"} → ${b.to_airport || "?"}${b.travel_date ? ` · ${b.travel_date}` : ""}`} />
          {b.tag_numbers?.length > 0 && (
            <Row icon={TagIcon} label="Bag tags" value={b.tag_numbers.join(", ")} />
          )}
        </div>

        {b.photo_url && (
          <div className="mt-4 rounded-2xl bg-card border border-border overflow-hidden shadow-warm-sm" data-testid="public-bag-photo">
            <div className="px-4 py-2.5 text-xs font-mono uppercase tracking-widest text-muted-foreground border-b border-border">Bag photo</div>
            <img src={b.photo_url} alt="Bag" className="w-full h-auto object-cover max-h-[420px]" loading="lazy" />
          </div>
        )}

        <p className="mt-5 text-xs text-center text-muted-foreground">
          Only minimal info is shown here for privacy. Thank you for helping return this bag.
        </p>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <span className="shrink-0 w-9 h-9 rounded-xl bg-secondary/60 text-primary inline-flex items-center justify-center">
      <Icon className="w-4 h-4" strokeWidth={2} />
    </span>
    <div className="min-w-0">
      <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-serif font-700 text-base text-primary leading-snug break-words">{value}</div>
    </div>
  </div>
);
